import { Camera, CheckCircle2, Clock, Info, LogIn, Trophy, UserCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type ApplicationStatus = "submitted" | "accepted" | "waitlisted" | "declined" | "withdrawn" | "completed";

interface TopShopStatusBarProps {
  isReady: boolean;
  isSignedIn: boolean;
  fullName: string;
  firstName: string;
  applicationStatus?: ApplicationStatus;
  photoCount: number;
  signInHref: string;
  onApply: () => void;
  registrationClosed?: boolean;
}

export default function TopShopStatusBar({
  isReady,
  isSignedIn,
  fullName,
  firstName,
  applicationStatus,
  photoCount,
  signInHref,
  onApply,
  registrationClosed = false,
}: TopShopStatusBarProps) {
  if (!isReady) return null;

  let Icon = LogIn;
  let message = "Already applied or have an EAB Connect account? Sign in to see your application.";
  let background = "bg-muted text-foreground";
  let buttonLabel = "Sign in";
  let buttonHref = signInHref;
  let buttonClassName = "border-foreground/30 bg-background text-foreground hover:bg-background/80";
  let applyButton = false;

  if (isSignedIn && !applicationStatus && registrationClosed) {
    Icon = Info;
    message = "Top Shop registration is closed. Your EAB Connect membership gives you training, tools and mentors.";
    background = "bg-primary text-primary-foreground";
    buttonLabel = "Go to your dashboard";
    buttonHref = "/dashboard";
    buttonClassName = "border-warning bg-warning text-foreground hover:bg-warning/90";
  } else if (isSignedIn && !applicationStatus) {
    Icon = UserCheck;
    message = `You're signed in as ${fullName}. You haven't applied to Top Shop yet.`;
    background = "bg-primary text-primary-foreground";
    buttonLabel = "Apply now";
    buttonHref = "";
    buttonClassName = "border-warning bg-warning text-foreground hover:bg-warning/90";
    applyButton = true;
  } else if (applicationStatus === "accepted") {
    Icon = Trophy;
    message = `Congratulations ${firstName}, you're in the Top Shop Challenge.`;
    background = "bg-success text-success-foreground";
    buttonLabel = "View application";
    buttonHref = "/topshop/application";
    buttonClassName = "border-background bg-background text-foreground hover:bg-background/90";
  } else if (applicationStatus === "completed") {
    Icon = Trophy;
    message = `Congratulations ${firstName}, you completed the Top Shop Challenge. Keep learning and growing on EAB Connect.`;
    background = "bg-success text-success-foreground";
    buttonLabel = "Go to EAB Connect";
    buttonHref = "/dashboard";
    buttonClassName = "border-background bg-background text-foreground hover:bg-background/90";
  } else if (applicationStatus === "withdrawn") {
    Icon = Info;
    message = `Thank you for taking part, ${firstName}. You still have full access to EAB Connect training and mentors.`;
    background = "bg-muted text-foreground";
    buttonLabel = "Go to EAB Connect";
    buttonHref = "/dashboard";
    buttonClassName = "border-foreground/30 bg-transparent text-foreground hover:bg-background";
  } else if (applicationStatus === "waitlisted") {
    Icon = Clock;
    message = `You're on the waitlist, ${firstName}. We'll let you know if a place opens up.`;
    background = "bg-warning text-foreground";
    buttonLabel = "View application";
    buttonHref = "/topshop/application";
    buttonClassName = "border-background bg-background text-foreground hover:bg-background/90";
  } else if (applicationStatus === "declined") {
    Icon = Info;
    message = `Thank you for applying, ${firstName}. You still have full access to EAB Connect training and mentors.`;
    background = "bg-muted text-foreground";
    buttonLabel = "Go to EAB Connect";
    buttonHref = "/dashboard";
    buttonClassName = "border-foreground/30 bg-transparent text-foreground hover:bg-background";
  } else if (applicationStatus && photoCount === 0) {
    Icon = Camera;
    message = `Your application is in, ${firstName}. Add photos of your shop to complete it.`;
    background = "bg-warning text-foreground";
    buttonLabel = "Add photos";
    buttonHref = "/topshop/application";
    buttonClassName = "border-background bg-background text-foreground hover:bg-background/90";
  } else if (applicationStatus) {
    Icon = CheckCircle2;
    message = `Your application is complete and under review, ${firstName}.`;
    background = "bg-primary text-primary-foreground";
    buttonLabel = "View application";
    buttonHref = "/topshop/application";
    buttonClassName = "border-background bg-background text-foreground hover:bg-background/90";
  }

  return (
    <div className={cn("w-full", background)}>
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
        <div className="flex min-w-0 items-start gap-3 md:items-center">
          <Icon className="mt-0.5 h-5 w-5 shrink-0 md:mt-0" aria-hidden="true" />
          <p className="font-medium leading-6">{message}</p>
        </div>
        {applyButton ? (
          <Button variant="outline" className={cn("w-full shrink-0 md:w-auto", buttonClassName)} onClick={onApply}>
            {buttonLabel}
          </Button>
        ) : (
          <Button asChild variant="outline" className={cn("w-full shrink-0 md:w-auto", buttonClassName)}>
            {buttonHref.startsWith("http") ? <a href={buttonHref}>{buttonLabel}</a> : <Link to={buttonHref}>{buttonLabel}</Link>}
          </Button>
        )}
      </div>
    </div>
  );
}